import React, { useMemo } from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import AbbreviateNumber from 'components/formatting/AbbreviateNumber';
import i18n from 'translation';
import ISOPercentage from 'components/formatting/ISOPercentage';

export interface BubbleDataPoint {
  x: number;
  y: number;
  size: number;
  name: string;
  color: string;
}

export interface BubbleChartProps {
  sizeLabel: string;
  xLabel: string;
  yLabel: string;
  dataPoints: BubbleDataPoint[];

  legendFontSize?: number;
  axisLabelFontSize?: number;
  axisNumberFontSize?: number;

  xAsPercent?: boolean;
  yAsPercent?: boolean;
  sizeAsPercent?: boolean;

  // In percentages (0-100), default is 30
  maxSize?: number;
}

export function BubbleChart(props: BubbleChartProps): React.JSX.Element {
  const theme = useTheme();

  const { sizeLabel,
          xLabel,
          yLabel,
          dataPoints,
          legendFontSize = 1.1,
          axisLabelFontSize = 1.5,
          axisNumberFontSize = 1,
          xAsPercent = false,
          yAsPercent = false,
          sizeAsPercent = false,
          maxSize = 50 } = props;

  const [hoveredBubbleIndex, setHoveredBubbleIndex] = React.useState<number | null>(null);

  interface DisplayBubble {
    dataPoint: BubbleDataPoint;
    xPct: number;
    yPct: number;
    sizePct: number;
  }

  interface DisplayBubbleInfo {
    maxXPct: number;
    maxXValue: number;
    maxYPct: number;
    maxYValue: number;
    displayBubbles: DisplayBubble[];
  }

  const displayBubbleInfo = useMemo(() => {
    if (!dataPoints || dataPoints.length === 0) {
      return {} as DisplayBubbleInfo;
    }

    // Sizes should be considered as areas, so convert them accordingly
    const diameters = dataPoints.map((dp) => {
      const size = dp.size || 0;
      return 2 * Math.sqrt(size / Math.PI);
    });

    const maxX = Math.max(...dataPoints.map(p => p.x));
    const minX = Math.min(...dataPoints.map(p => p.x));
    const maxY = Math.max(...dataPoints.map(p => p.y));
    const minY = Math.min(...dataPoints.map(p => p.y));
    const minDiameter = Math.min(...diameters);
    const maxDiameter = Math.max(...diameters);

    // First normalize all positions to 0-100 and sizes to maxSize
    let displayBubbles: DisplayBubble[] = dataPoints.map((dp, index) => {
      const diameter = diameters[index] || 0;
      const x = dp.x || 0;
      const y = dp.y || 0;
      return {
        dataPoint: dp,
        xPct: x / (maxX || 1) * 100 || 0,
        yPct: y / (maxY || 1) * 100 || 0,
        sizePct: diameter / maxDiameter * maxSize
      };
    });

    // Display bubbles will now be rendered outside the bounds of the box on the top and right sides,
    // so we need to adjust the positions and sizes to account for that
    const maxNormalizedX = Math.max(...displayBubbles.map(db => (db.xPct || 0) + (db.sizePct || 0) / 2));
    const maxNormalizedY = Math.max(...displayBubbles.map(db => (db.yPct || 0) + (db.sizePct || 0) / 2));

    displayBubbles = displayBubbles.map(db => {
      return {
        ...db,
        xPct: (db.xPct / maxNormalizedX) * 100,
        yPct: (db.yPct / maxNormalizedY) * 100,
        sizePct: (db.sizePct / maxNormalizedX) * 100
      };
    });

    const results = {
      maxXPct: Math.max(...displayBubbles.map(db => db.xPct)),
      maxYPct: Math.max(...displayBubbles.map(db => db.yPct)),
      maxXValue: Math.max(...dataPoints.map(db => db.x)),
      maxYValue: Math.max(...dataPoints.map(db => db.y)),
      displayBubbles
    } as DisplayBubbleInfo;

    return results;
  }, [dataPoints, maxSize]);

  const activeBubble = useMemo(() => {
    if (displayBubbleInfo?.displayBubbles && hoveredBubbleIndex !== null) {
      return displayBubbleInfo.displayBubbles[hoveredBubbleIndex];
    }
    return null;
  }, [hoveredBubbleIndex, dataPoints]);

  const displayRows = useMemo(() => {
    const isHovered = hoveredBubbleIndex !== null;
    const opacityValue = isHovered ? 0.3 : 0.7;
    const sortedDisplayBubbles = displayBubbleInfo?.displayBubbles.sort((a, b) => b.sizePct - a.sizePct) || [];

    return sortedDisplayBubbles.map((displayBubble, index) => {
      return (
        <Box key={index} sx={{
          position: 'absolute',
          borderRadius: '50%',
          left: `${displayBubble.xPct}%`,
          top: `${100 - displayBubble.yPct}%`,
          aspectRatio: '1 / 1',
          transform: `translate(-50%, -50%)`, // center the bubble on the coordinates
          mixBlendMode: 'plus-darker',
          backgroundColor: displayBubble.dataPoint.color,
          opacity: hoveredBubbleIndex === index ? 1.0 : opacityValue,
          cursor: 'pointer',
          // Container query to maintain circular shape
          '@container bubble-chart-contents (max-aspect-ratio: 1/1)': {
            width: `${displayBubble.sizePct}%`,
          },
          '@container bubble-chart-contents (min-aspect-ratio: 1/1)': {
            height: `${displayBubble.sizePct}%`,
          }
        }}
        onMouseEnter={() => setHoveredBubbleIndex(index)}
        onMouseLeave={() => setHoveredBubbleIndex(null)}
        />
      );
    });
  }, [dataPoints, hoveredBubbleIndex]);

  return (
    <Tooltip followCursor={true}
      open={activeBubble?.dataPoint !== undefined}
      title={
        <Box>
          <Box sx={{ pt: 0.3, pb: 1, fontWeight:"bold", fontSize:"1.1rem" }}>
            <Box sx={{ display: "inline-block", color: activeBubble?.dataPoint?.color, mr:1, verticalAlign: "middle"}} >&#11044;</Box>
            <Box sx={{ display:"inline-block", pt: 0.5,  verticalAlign: "middle"}}>{activeBubble?.dataPoint?.name}</Box>
          </Box>
          {i18n.t("widget-types.bubble-comparison.size")}:<br />
          {sizeAsPercent &&
            <><Typography sx={{display: "inline-block", color: theme.colors.primary.main, mr: 1, ml: 1}} variant="h4">{Math.round(activeBubble?.dataPoint.size || 0)}%</Typography> </>
          }
          {!sizeAsPercent &&
            <AbbreviateNumber sx={{color: theme.colors.primary.main, mr: 1, ml: 1}} typographyVariant="h4" value={activeBubble?.dataPoint.size || 0} />
          }
          {sizeLabel}<br/>
          X:<br />
          {xAsPercent &&
            <><Typography sx={{display: "inline-block", color: theme.colors.primary.main, mr: 1, ml: 1}} variant="h4">{Math.round(activeBubble?.dataPoint.x || 0)}%</Typography> </>
          }
          {!xAsPercent &&
            <AbbreviateNumber sx={{color: theme.colors.primary.main, mr: 1, ml: 1}} typographyVariant="h4" value={activeBubble?.dataPoint.x || 0} />
          }
          {xLabel}<br/>
          Y:<br />
          {yAsPercent &&
            <><Typography sx={{display: "inline-block", color: theme.colors.primary.main, mr: 1, ml: 1}} variant="h4">{Math.round(activeBubble?.dataPoint.y || 0)}%</Typography> </>
          }
          {!yAsPercent &&
            <AbbreviateNumber sx={{color: theme.colors.primary.main, mr: 1, ml: 1}}  typographyVariant="h4" value={activeBubble?.dataPoint.y || 0} />
          }
          {yLabel}
        </Box>
      }>
      <Box>
        <Box sx={{ position: 'absolute', display: 'grid',
          top: 0, left: 0, width: '100%', height: '100%', pt: 2,
          gridTemplateColumns: `min-content 24px auto min-content`,
          gridTemplateRows: `auto 24px min-content min-content`,
        }}>
        <Box sx={{writingMode: 'sideways-lr', fontSize: `${axisLabelFontSize}rem`, textAlign: 'center', color:theme.colors.secondary.dark}}>
          {yLabel}
        </Box>
        {activeBubble && (
        <Box sx={{fontSize: `${axisNumberFontSize}rem`, color:theme.colors.secondary.main, writingMode: 'sideways-lr', position: 'relative'}}>
          <Box sx={{position: 'absolute', top: `${100.0 - activeBubble.yPct}%`, transform: 'translateY(-50%)'}}>
            <AbbreviateNumber typographyVariant="h5" value={activeBubble.dataPoint.y} />
          </Box>
        </Box>
        )}
        {!activeBubble && (
        <Box sx={{fontSize: `${axisNumberFontSize}rem`, color:theme.colors.secondary.main, position: 'relative'}}>
          <Box sx={{
            position: 'absolute',
            top: `${100.0 - displayBubbleInfo.maxYPct}%`,
            transform: 'translateY(-50%)',
            writingMode: 'sideways-lr',
            textAlign: 'right' }}>
            <AbbreviateNumber typographyVariant="h5" value={displayBubbleInfo?.maxYValue as number} />
          </Box>
          <Box sx={{
            position: 'absolute',
            top: `100%`,
            transform: 'translateY(-100%)',
            writingMode: 'sideways-lr',
            textAlign: 'left'}}>
            <AbbreviateNumber typographyVariant="h5" value={0} />
          </Box>
        </Box>
        )}
        <Box sx={{
            borderLeft: `1px solid ${theme.colors.namedColors.chartElement}`,
            borderBottom: `1px solid ${theme.colors.namedColors.chartElement}`,
            position:"relative", overflow: 'hidden',
            containerType: 'size', containerName: 'bubble-chart-contents'  }}>
          <Box sx={{
            position: 'absolute',
            border: `1px solid ${theme.colors.namedColors.chartElement}`,
            top: `${100.0 - (activeBubble ? activeBubble.yPct : displayBubbleInfo.maxYPct)}%`,
            left: "0",
            width: activeBubble ? "100%" : "10px",
            height: `0px` }}>
          </Box>
          <Box sx={{
            position: 'absolute',
            border: `1px solid ${theme.colors.namedColors.chartElement}`,
            left: `${activeBubble ? activeBubble.xPct : displayBubbleInfo.maxXPct}%`,
            bottom: "0%",
            width: `0px`,
            height: activeBubble ? "100%" : "10px" }}>
          </Box>
          {displayRows}
        </Box>
        <Box sx={{writingMode: 'sideways-rl', fontSize: `${axisLabelFontSize}rem`, textAlign: 'center', color:theme.colors.secondary.dark}}>
          {i18n.t("widget-types.bubble-comparison.size")}: {sizeLabel}
        </Box>
        <Box sx={{gridColumnEnd: 'span 2'}}></Box>
          {activeBubble && (
            <Box sx={{fontSize: `${axisNumberFontSize}rem`, textAlign: 'center', color:theme.colors.secondary.main, position: 'relative'}}>
              <Box sx={{display: "inline-block", position: 'absolute', left: `${activeBubble.xPct}%`, transform: 'translateX(-50%)'}}>
                <AbbreviateNumber typographyVariant="h5" value={activeBubble.dataPoint.x} />
              </Box>
            </Box>
          )}
          {!activeBubble && (
            <Box sx={{fontSize: `${axisNumberFontSize}rem`, textAlign: 'center', color:theme.colors.secondary.main, display: 'grid', gridTemplateColumns: '1fr 1fr', position: 'relative'}}>
              <Box sx={{
                display: "inline-block",
                position: 'absolute',
                top: 0, left: 0,
                textAlign: 'left' }}>
                <AbbreviateNumber typographyVariant="h5" value={0} />
              </Box>
              <Box sx={{
                display: "inline-block",
                position: 'absolute',
                top: 0,
                left: `${displayBubbleInfo?.maxXPct}%`,
                transform: 'translateX(-50%)',
                textAlign: 'right' }}>
                {xAsPercent &&
                  <ISOPercentage typographyVariant={"h5"} value={displayBubbleInfo?.maxXValue/100} />
                }
                {!xAsPercent &&
                  <AbbreviateNumber typographyVariant="h5" value={displayBubbleInfo?.maxXValue as number} />
                }
              </Box>
            </Box>
          )}
          <Box></Box>
          <Box sx={{gridColumnEnd: 'span 2'}}></Box>
          <Box sx={{fontSize: `${axisLabelFontSize}rem`, textAlign: 'center', color:theme.colors.secondary.dark}}>
            {xLabel}
          </Box>
          <Box></Box>
          <Box sx={{gridColumnEnd: 'span 4', fontSize: `${legendFontSize}rem`, textAlign: 'center', pt: 1, pb: 0.5}}>
            {dataPoints.map((dataPoint, index) => {
              return (
                <Box key={index}
                  sx={{ display: 'inline-block', mx: 0.5, mr: 2, cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredBubbleIndex(index)}
                  onMouseLeave={() => setHoveredBubbleIndex(null)}
                  >
                  <Box sx={{ display: 'inline-block', width: '1rem', height: '1rem', color: dataPoint.color, mr:1}} >&#11044;</Box>
                  <span style={{ color: theme.colors.secondary.main }}>{dataPoint.name}</span>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Tooltip>
  );
}